import { comboTierLabel } from '~/modules/game-engine/game-state/helpers/combo';

/**
 * A combo is a number that steps up in size once per tier, never a meter and never a pulse per
 * note. Thresholds come from `combo.ts` so the tier shown while singing is the one reported after.
 */
export const COMBO_TIER_SIZE_PX: Record<string, number> = {
  STREAK: 30,
  HOT: 36,
  'ON FIRE': 42,
  LEGENDARY: 48,
};

export const UNTIERED_COMBO_SIZE_PX = 24;

/** Two notes in a row is the shortest run worth putting on screen. */
export const MIN_SHOWN_COMBO = 2;

/** How long "combo lost" stands in for the number that was there. */
export const COMBO_LOST_MS = 3_000;

export type ComboDisplay =
  | { kind: 'combo'; count: number; label: string; sizePx: number; tiered: boolean }
  | { kind: 'lost' }
  | null;

export const getComboDisplay = ({
  combo,
  lostAt,
  now,
}: {
  combo: number;
  /** When this player's run last ended in a miss, or null if it never has. */
  lostAt: number | null;
  now: number;
}): ComboDisplay => {
  if (combo >= MIN_SHOWN_COMBO) {
    const tier = comboTierLabel(combo);

    return {
      kind: 'combo',
      count: combo,
      label: tier ? tier.toLowerCase() : 'combo',
      sizePx: tier ? COMBO_TIER_SIZE_PX[tier] : UNTIERED_COMBO_SIZE_PX,
      tiered: tier !== null,
    };
  }

  if (lostAt !== null && now - lostAt < COMBO_LOST_MS) return { kind: 'lost' };

  return null;
};

export interface ComboSnapshot {
  current: number;
  /** When the run last ended in a miss, or null if it never has. */
  lostAt: number | null;
  /** Notes sung so far, only to tell a restart from a miss - both drop the run to nothing. */
  noteCount: number;
}

export const trackCombo = (
  previous: ComboSnapshot | undefined,
  { current, noteCount, now }: { current: number; noteCount: number; now: number },
): ComboSnapshot => {
  // Fewer notes than last look means the song restarted, and nothing was lost by singing badly.
  const restarted = previous !== undefined && noteCount < previous.noteCount;

  if (previous === undefined || restarted) return { current, noteCount, lostAt: null };

  // A drop rather than a zero: at ten looks a second the first note of the next run can land in
  // the same look as the miss that ended the last one.
  return current < previous.current
    ? { current, noteCount, lostAt: now }
    : { current, noteCount, lostAt: previous.lostAt };
};
